import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaSearch, FaHandshake, FaRocket, FaHeadset, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './JourneySection.css';

const STEPS = [
  {
    id: '01',
    icon: <FaSearch />,
    title: 'Discover Your Needs',
    desc: 'Browse our full suite of software, hardware, and IT solutions tailored to your business stage and industry.',
    cta: 'Explore Services',
    route: '/ServicePage',
    color: '#6b7a52',
  },
  {
    id: '02',
    icon: <FaHandshake />,
    title: 'Book a Free Consultation',
    desc: 'Connect with our certified experts who analyze your workflow and recommend the most impactful solution.',
    cta: 'Book Consultation',
    route: '/contact',
    color: '#96a67e',
  },
  {
    id: '03',
    icon: <FaRocket />,
    title: 'Seamless Implementation',
    desc: 'We deploy, configure, and integrate your chosen solutions — zero disruption to your existing operations.',
    cta: 'Our Solutions',
    route: '/solutions',
    color: '#5a6e3f',
  },
  {
    id: '04',
    icon: <FaHeadset />,
    title: '24/7 Ongoing Support',
    desc: 'Our dedicated team stands by you post-launch with training, maintenance, and priority escalations.',
    cta: 'Contact Support',
    route: '/contact',
    color: '#4a5c35',
  },
];

const StepCard = ({ step, index, isLast, onCardClick, isSecretUnlocked }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const navigate = useNavigate();

  return (
    <div className="journey-step-wrapper" ref={ref}>
      <motion.div
        className={`journey-step-card ${isSecretUnlocked ? 'secret-pulse' : ''}`}
        initial={{ opacity: 0, y: 40 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: index * 0.15, ease: [0.25, 0.1, 0.25, 1] }}
        onClick={() => onCardClick(step.id)}
        style={{ cursor: 'pointer' }}
      >
        <div className="journey-step-number">{step.id}</div>
        <div className="journey-step-icon" style={{ background: `${step.color}18`, color: step.color }}>
          {step.icon}
        </div>
        <h3 className="journey-step-title">{step.title}</h3>
        <p className="journey-step-desc">{step.desc}</p>
        <button
          className="journey-step-cta"
          onClick={(e) => {
            e.stopPropagation();
            navigate(step.route);
          }}
        >
          {step.cta} <FaArrowRight />
        </button>
      </motion.div>

      {!isLast && (
        <motion.div
          className="journey-connector"
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.5, delay: index * 0.15 + 0.4 }}
        />
      )}
    </div>
  );
};

const JourneySection = () => {
  const titleRef = useRef(null);
  const titleInView = useInView(titleRef, { once: true, margin: '-60px' });
  
  // Easter Egg: Click cards in order 01 -> 02 -> 03 -> 04
  const [clickSequence, setClickSequence] = useState([]);
  const [showCertification, setShowCertification] = useState(false);

  const handleCardClick = (stepId) => {
    setClickSequence(prev => {
      const nextSequence = [...prev, stepId];
      
      // Keep only last 4 clicks
      const trimmed = nextSequence.slice(-4);
      
      if (trimmed.join(',') === '01,02,03,04') {
        setShowCertification(true);
        return [];
      }
      return trimmed;
    });
  };


  return (
    <section className="journey-section" id="journey">
      <div className="journey-bg-accent" aria-hidden="true" />

      <motion.div
        ref={titleRef}
        className="journey-header"
        initial={{ opacity: 0, y: 30 }}
        animate={titleInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
      >
        <span className="section-eyebrow">How It Works</span>
        <h2 className="section-title">
          Your Path to <span className="highlight-text">Enterprise Excellence</span>
        </h2>
        <p className="section-subtitle">
          From discovery to long-term partnership — every step is designed around your success.
        </p>
      </motion.div>

      <div className="journey-steps-row">
        {STEPS.map((step, i) => (
          <StepCard
            key={step.id}
            step={step}
            index={i}
            isLast={i === STEPS.length - 1}
            onCardClick={handleCardClick}
            isSecretUnlocked={showCertification}
          />
        ))}
      </div>

      {/* Easter Egg: Certification Modal */}
      {showCertification && (
        <div
          className="secret-cert-overlay"
          onClick={() => setShowCertification(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 999990,
            background: 'rgba(10, 15, 30, 0.7)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
          }}
        >
          <div
            className="secret-cert-card"
            onClick={e => e.stopPropagation()}
            style={{
              background: '#0d1627',
              border: '2px solid #8da46e',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '540px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 40px rgba(141, 164, 110, 0.2)',
              position: 'relative',
              animation: 'ctx-menu-in 0.4s cubic-bezier(0.16,1,0.3,1) forwards',
              color: '#f0f6fc',
              fontFamily: "'Courier New', Courier, monospace"
            }}
          >
            {/* Hologram badge */}
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'radial-gradient(circle, #8da46e, #4a5c35)',
              margin: '0 auto 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2rem', boxShadow: '0 0 20px rgba(141, 164, 110, 0.5)'
            }}>🛡️</div>

            <h3 style={{ color: '#8da46e', fontSize: '1.4rem', fontWeight: 'bold', margin: '0 0 10px 0', letterSpacing: '0.05em' }}>
              iMatrix Systems Operational Seal
            </h3>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 24px 0', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Integrity Verified &bull; ISO Compliance
            </p>
            <div style={{
              borderTop: '1px solid rgba(255,255,255,0.08)',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              padding: '20px 0',
              margin: '0 0 24px 0',
              textAlign: 'left',
              fontSize: '0.85rem',
              lineHeight: 1.6,
              color: '#cbd5e1'
            }}>
              <div>[SYSTEM CERTIFICATE OF ALIGNMENT]</div>
              <div style={{ marginTop: 8 }}>&bull; Stage 01: Discover &amp; Diagnose &mdash; complete</div>
              <div>&bull; Stage 02: Consultative Mapping &mdash; complete</div>
              <div>&bull; Stage 03: Deployment &amp; Engineering &mdash; complete</div>
              <div>&bull; Stage 04: Gold SLA Priority Support &mdash; active</div>
              <div style={{ marginTop: 12, fontSize: '0.78rem', color: '#64748b' }}>
                Ref code: IMT-SEC-2006-OK
              </div>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic', marginBottom: 20 }}>
              “Systems synchronized. The loop is complete.”
            </p>
            <button
              onClick={() => setShowCertification(false)}
              style={{
                background: '#8da46e', border: 'none', borderRadius: '8px',
                padding: '10px 24px', cursor: 'pointer', color: '#0d1627',
                fontWeight: 'bold', fontSize: '0.9rem', transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#a3ba84'}
              onMouseLeave={e => e.currentTarget.style.background = '#8da46e'}
            >
              System Sync Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default JourneySection;
